// Pure, reusable field registry for the electronic-module AI enrichment
// surfaces — the per-module review in ElectronicModuleEditorModal, the preview
// in AiElectronicModuleEnrichPanel, and the bulk review grid the modules
// listing opens through BulkEntityAIEnhance.
//
// The module analogue of winderEnrichFields.ts, and shaped by the same two
// facts:
//
//  1. `enrichElectronicModule` returns a PER-FIELD result map (`{ displayType:
//     { value, confidence, reasoning }, … }`), like `enrichWinder` and unlike
//     `enrichCalibre`'s single overall confidence. Confidence is read per cell.
//  2. `ElectronicModule` is FLAT — no `data.*` sub-map like CalibreData — so a
//     patch key is just the field name. No dotted paths, no spec remaps.
//
// Every function here is pure: it reads a stored module and a result object and
// returns plain values, so the same code backs the single-module apply and the
// headless bulk write.

import type { ElectronicModule } from '../types';
import type { ModuleEnrichResult } from './firebase';

export type ModuleFieldType = 'str' | 'int' | 'strArr';

export interface ModuleEnrichFieldSpec {
  /** Key as it appears in the enrichElectronicModule result AND on the module. */
  key: string;
  /** Human label — column header in the review grid, row label in the diff. */
  label: string;
  type: ModuleFieldType;
}

/**
 * The canonical module name.
 *
 * Only ever a COLUMN on a genuine rename (see `suggestedModuleKeys`), and it
 * never defaults to checked — see `MODULE_NEVER_DEFAULT_KEYS`.
 */
export const MODULE_NAME_KEY = 'name';

/**
 * The AI description. The result carries it as `aiDescription` or, from older
 * prompt revisions, `description`; both resolve here, and it is written to
 * `aiDescription` so a curator's hand-written `description` is never touched.
 */
export const MODULE_DESCRIPTION_KEY = 'aiDescription';

/**
 * Ignores the fill/overwrite write-mode and defaults to checked even when the
 * module already has one — a regenerated description is the point of a re-run.
 * Still gated on the confidence threshold.
 */
export const MODULE_ALWAYS_APPLY_KEYS = new Set<string>([MODULE_DESCRIPTION_KEY]);

/**
 * Never defaults to checked, in either write mode.
 *
 * Renaming is the one module edit with consequences beyond the field: the name
 * is what every curator recognises the module by, and `buildElectronicModuleDocId`
 * derives the doc id from it at create time, so the id and the name diverge
 * silently afterwards. Accepting fifty of those because "overwrite" was ticked
 * is not a decision anyone made. The column still appears and can be ticked by
 * hand, one row at a time.
 */
export const MODULE_NEVER_DEFAULT_KEYS = new Set<string>([MODULE_NAME_KEY]);

// Order here is the column order in the review grid: identity first, then
// display → power → features → provenance, with the long description last, so
// a reviewer reads the grid roughly in the order the editor renders the form.
const SPEC_LIST: ModuleEnrichFieldSpec[] = [
  { key: MODULE_NAME_KEY, label: 'Name', type: 'str' },
  { key: 'generation', label: 'Generation', type: 'str' },
  { key: 'displayType', label: 'Display type', type: 'str' },
  { key: 'lcdType', label: 'LCD type', type: 'str' },
  { key: 'displayLayout', label: 'Display layout', type: 'str' },
  { key: 'viewingAngle', label: 'Viewing angle', type: 'str' },
  { key: 'displayColor', label: 'Display colour', type: 'str' },
  { key: 'textColor', label: 'Text colour', type: 'str' },
  { key: 'lightType', label: 'Light type', type: 'str' },
  { key: 'powerSource', label: 'Power source', type: 'str' },
  { key: 'battery', label: 'Battery', type: 'str' },
  { key: 'batteryLifeMonths', label: 'Battery life (months)', type: 'int' },
  { key: 'chargeTimeHours', label: 'Charge time (hours)', type: 'int' },
  { key: 'syncSource', label: 'Sync source', type: 'str' },
  { key: 'timeSystems', label: 'Time systems', type: 'strArr' },
  { key: 'dstSupport', label: 'DST support', type: 'str' },
  { key: 'calendarType', label: 'Calendar type', type: 'str' },
  { key: 'stopwatchPrecision', label: 'Stopwatch precision', type: 'str' },
  { key: 'alarmTypes', label: 'Alarm types', type: 'strArr' },
  { key: 'sensors', label: 'Sensors', type: 'strArr' },
  { key: 'source', label: 'Source', type: 'str' },
  { key: 'sourceUrl', label: 'Source URL', type: 'str' },
  { key: MODULE_DESCRIPTION_KEY, label: 'AI description', type: 'str' },
];

/*
 * Three fields the model may answer that this registry deliberately does NOT
 * write, so none of them is ever a column:
 *
 *  - `manufacturer` — arrives as a free-text maker name, while the document
 *    needs a `manufacturerId` FK. Guessing an id from a name is how a module
 *    ends up attached to the wrong maker; the manufacturer picker (and the bulk
 *    Set Manufacturer action) resolve it against the catalogue instead.
 *  - `images` — bytes, not a value. Nothing in a bulk review can verify that a
 *    proposed image is of this module.
 *  - `alsoKnownAs` — aliases are MATCHING keys, not content. They decide which
 *    imported strings resolve to this module, so a plausible-sounding wrong
 *    number quietly captures other modules' references; asked for module 3229,
 *    the model offered two numbers that were not it. The alias list grows from
 *    two places that know rather than guess: the importer, which files the
 *    string it actually saw, and a rename here, which demotes the name the
 *    catalogue was already using. The enrich callable no longer offers the
 *    field at all (functions/src/aiEnrich.ts); this list is the second lock.
 */

export const MODULE_ENRICH_FIELDS: Record<string, ModuleEnrichFieldSpec> =
  Object.fromEntries(SPEC_LIST.map((s) => [s.key, s]));

export const MODULE_ENRICH_FIELD_ORDER: string[] = SPEC_LIST.map((s) => s.key);

export function getModuleFieldSpec(key: string): ModuleEnrichFieldSpec | undefined {
  return MODULE_ENRICH_FIELDS[key];
}

export function getModuleFieldLabel(key: string): string {
  return MODULE_ENRICH_FIELDS[key]?.label ?? key;
}

/** Normalise a raw suggestion to the type the field stores; null = unusable. */
export function coerceModuleValue(key: string, value: unknown): unknown {
  const spec = MODULE_ENRICH_FIELDS[key];
  if (!spec || value === undefined || value === null) return null;

  if (spec.type === 'int') {
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    return Number.isFinite(n) ? n : null;
  }

  if (spec.type === 'strArr') {
    const raw = Array.isArray(value) ? value : [value];
    const list = raw
      .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
      .map((v) => String(v).trim())
      .filter(Boolean);
    return list.length ? [...new Set(list)] : null;
  }

  const s = typeof value === 'string' ? value.trim() : String(value).trim();
  return s ? s : null;
}

/**
 * Render a value for display AND for the agreement comparison.
 *
 * The same formatter has to serve both sides: the grid compares a formatted
 * proposal against `getModuleExisting` to decide whether a cell changes
 * anything, so a second formatter here would make agreeing values read as
 * conflicts and rewrite documents the model merely confirmed.
 */
export function formatModuleValue(v: unknown): string {
  if (v === undefined || v === null) return '';
  if (Array.isArray(v)) return v.map((x) => String(x)).join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

/** The module's current value for a field as a display string; '' means empty. */
export function getModuleExisting(entry: ElectronicModule, key: string): string {
  const v = (entry as unknown as Record<string, unknown>)[key];
  return formatModuleValue(v);
}

/**
 * Read the proposed value for one field out of an enrichElectronicModule result.
 *
 * The callable puts multi-value fields under `values` (plural) and scalars under
 * `value`; falling back across both keeps a scalar-shaped entry for an array
 * field (or vice versa) usable rather than silently dropped — the coercion
 * normalises either shape.
 */
export function getModuleSuggestion(result: ModuleEnrichResult, key: string): unknown {
  const entry = resultEntry(result, key);
  if (!entry) return undefined;
  return entry.values ?? entry.value;
}

/** Per-field confidence from the result; 'low' when absent. */
export function getModuleConfidence(result: ModuleEnrichResult, key: string): string {
  const c = resultEntry(result, key)?.confidence;
  return typeof c === 'string' && c ? c.toLowerCase() : 'low';
}

/** Per-field reasoning from the result; '' when absent. */
export function getModuleReasoning(result: ModuleEnrichResult, key: string): string {
  const r = resultEntry(result, key)?.reasoning;
  return typeof r === 'string' ? r.trim() : '';
}

/** The result entry backing a key, resolving the description's two spellings. */
function resultEntry(result: ModuleEnrichResult, key: string) {
  const direct = result[key];
  if (direct && typeof direct === 'object') return direct;
  if (key === MODULE_DESCRIPTION_KEY) {
    const legacy = result.description;
    if (legacy && typeof legacy === 'object') return legacy;
  }
  return undefined;
}

/**
 * Every field key this result proposes a usable value for, in registry order.
 * Drives which columns the review grid renders — a field the model omitted (or
 * answered unusably) never becomes a column.
 *
 * `name` is the exception: a proposal that merely repeats the stored name is
 * not a rename, and a column of identical names would be noise in every grid.
 */
export function suggestedModuleKeys(
  result: ModuleEnrichResult,
  entry?: ElectronicModule,
): string[] {
  return MODULE_ENRICH_FIELD_ORDER.filter((key) => {
    const coerced = coerceModuleValue(key, getModuleSuggestion(result, key));
    if (coerced === null || coerced === undefined) return false;
    if (key === MODULE_NAME_KEY && entry) {
      return String(coerced).trim().toLowerCase() !== (entry.name || '').trim().toLowerCase();
    }
    return true;
  });
}

/**
 * Build the Firestore patch for a set of accepted cells of a single module.
 *
 * `ElectronicModule` is flat, so each accepted key maps to one top-level field.
 * Keys whose value can't be coerced are skipped, so `{}` back means "nothing
 * usable was accepted" and the caller should skip the write entirely rather than
 * stamping a no-op `updatedAt`.
 *
 * `manufacturerId` is not writable here by construction — it isn't in the
 * registry.
 *
 * A rename writes the name and NOTHING ELSE. Unlike `buildCalibrePatch`, which
 * demotes the outgoing name into the alias arrays, aliases here are the
 * curator's: they are matching keys, and which of them a module should carry is
 * a judgement about the import corpus rather than a side effect of accepting a
 * name. Worth knowing when renaming in bulk — an import string that matched the
 * old name stops matching until an alias carries it, which the importer does
 * the next time it links that module.
 */
export function buildModulePatch(
  /**
   * Unused, and kept anyway: `buildWinderPatch` takes the same three arguments,
   * the dialog's descriptor types the pair together, and a field that DOES need
   * the stored value (an append-rather-than-replace list, say) would otherwise
   * force a signature change at every call site.
   */
  _entry: ElectronicModule,
  keys: string[],
  result: ModuleEnrichResult,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of keys) {
    const spec = MODULE_ENRICH_FIELDS[key];
    if (!spec) continue;
    const value = coerceModuleValue(key, getModuleSuggestion(result, key));
    if (value === null || value === undefined) continue;
    patch[key] = value;
  }
  return patch;
}
